import React from 'react';
import { MatchupTeamEntry } from '@/api/schemas';
import { getAbbreviation } from '@/utils/teams';

// ============================================================================
// Offensive ranks table — two rows (home + away) for one match. Visual parity
// with MatchupDefenseTable: same card chrome, same per-stat columns, same
// yellow-accent headline column. Differences:
//   - Heading: "OFFENSIVE RANKS" instead of "DEFENSIVE RANKS"
//   - Headline column: PRK (production rank) sourced from `offensive_ranks.offensive_rank`
//     instead of OPRK. Per-stat ranks come from `offensive_ranks` instead of `ranks`.
//   - Sort semantics flip server-side: rank 1 = best offense (highest production).
//     Visual rendering of rank numbers is identical — '1st', '2nd', etc.
// ============================================================================

interface MatchupOffenseTableProps {
  home: MatchupTeamEntry;
  away: MatchupTeamEntry;
}

// Same stat keys as defense, in same order, so columns line up visually when
// the two tables are stacked one above the other.
const STAT_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'Goal', label: 'GL' },
  { key: 'ShotOnTarget', label: 'SoT' },
  { key: 'Shot', label: 'Sh' },
  { key: 'GCA', label: 'GCA' },
  { key: 'SCA', label: 'SCA' },
  { key: 'ProgPass', label: 'IP' },
  { key: 'ProgCarry', label: 'IC' },
  { key: 'Dispossessed', label: 'DIS' },
];

const ordinalize = (n: number): string => {
  const abs = Math.abs(n);
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (abs % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const formatRank = (v: number | null | undefined): string =>
  v == null ? '—' : ordinalize(v);

const RankCell: React.FC<{ value: number | null | undefined; highlight?: boolean }> = ({
  value,
  highlight,
}) => (
  <td
    className={[
      'px-2 py-3 sm:px-3 sm:py-4 text-center whitespace-nowrap',
      highlight
        ? 'font-heading font-bold text-lg sm:text-xl tracking-wider text-accent-yellow bg-accent-yellow/5'
        : 'font-mono font-medium text-sm text-gray-200',
      value == null ? (highlight ? '' : 'text-gray-600') : '',
    ].join(' ')}
  >
    {formatRank(value)}
  </td>
);

const TeamRow: React.FC<{ team: MatchupTeamEntry; isLast: boolean; isEven: boolean }> = ({
  team,
  isLast,
  isEven,
}) => {
  const abbr = getAbbreviation(team.team_name) || team.team_name;
  // offensive_ranks may be missing on responses from older backend versions; treat
  // every cell as null in that case rather than crashing.
  const offensive = team.offensive_ranks ?? {};
  return (
    <tr
      className={[
        isEven ? 'bg-primary-dark/30' : '',
        isLast ? '' : 'border-b border-gray-700/40',
      ].join(' ')}
    >
      {/* Team (sticky) */}
      <td className="py-3 sm:py-4 pl-2 sm:pl-6 pr-2 text-left whitespace-nowrap sticky left-0 bg-surface-card z-[5]">
        <span className="font-body font-bold text-[13px] sm:text-base text-white tracking-tight">
          {abbr}
        </span>
        <span className="ml-1.5 sm:ml-2 inline-block align-middle px-1.5 sm:px-2 py-0 sm:py-0.5 rounded bg-gray-200/10 text-gray-300 font-mono font-bold text-micro sm:text-badge tracking-wider whitespace-nowrap">
          {team.team_name}
        </span>
      </td>
      {/* Headline production rank (highlighted) */}
      <RankCell value={offensive.offensive_rank} highlight />
      {/* Per-stat offensive ranks */}
      {STAT_COLUMNS.map(({ key }) => (
        <RankCell key={key} value={offensive[key]} />
      ))}
    </tr>
  );
};

const MatchupOffenseTable: React.FC<MatchupOffenseTableProps> = ({ home, away }) => {
  return (
    <div className="w-full mb-4">
      <div className="relative overflow-hidden rounded-lg border border-accent-yellow/60 bg-surface-card shadow-[0_10px_40px_rgba(0,0,0,0.35)]">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-accent-yellow/10 border-b border-accent-yellow/60">
                <th
                  className="text-left pl-2 sm:pl-6 py-3 sm:py-4 font-heading font-bold text-sm sm:text-lg tracking-[1.5px] sm:tracking-[2px] uppercase text-white whitespace-nowrap sticky left-0 bg-surface-raised z-10"
                >
                  OFFENSIVE RANKS
                </th>
                <th className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-accent-yellow/10 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap">
                  PRK
                </th>
                {STAT_COLUMNS.map(({ label }) => (
                  <th
                    key={label}
                    className="font-heading font-bold text-caption sm:text-xs tracking-[2px] uppercase text-accent-yellow bg-primary-dark/60 text-center px-2 sm:px-3 py-3 sm:py-4 whitespace-nowrap"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TeamRow team={home} isEven={false} isLast={false} />
              <TeamRow team={away} isEven={true} isLast={true} />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MatchupOffenseTable;
